<template>
  <div class="space-y-4">
    <!-- Page header -->
    <div class="flex items-center gap-3">
      <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
        <span class="material-icons-round">manage_accounts</span>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-800">Users</h2>
        <p class="text-sm text-gray-500">Manage admin users and their access.</p>
      </div>
    </div>
    <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center space-x-1 text-gray-500">
          <span class="text-base material-icons-round">filter_list</span>
          <span class="text-xs font-semibold tracking-widest uppercase">Filters</span>
        </div>
        <router-link
          to="/accounts/add"
          class="inline-flex items-center h-10 px-4 text-sm font-semibold text-white rounded-xl shadow-sm bg-taxiye-500 transition-colors duration-150 hover:bg-taxiye-600"
        >
          <span class="mr-1 text-base material-icons-round">add</span>
          Add User
        </router-link>
      </div>

      <div class="flex flex-wrap items-end gap-4">
        <!-- Search -->
        <div class="relative w-full sm:w-64">
          <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Search</label>
          <div class="relative mt-1.5">
            <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <span class="text-base text-gray-500 material-icons-round">search</span>
            </span>
            <input
              @keyup="search"
              class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="Search users..."
            />
          </div>
        </div>
      </div>
    </div>

    <div class="mt-4 overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm">
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="bg-gray-100 border-b border-gray-200">
              <th
                v-for="col in ['ID', 'Full Name', 'Email', 'Role', 'Status', '']"
                :key="`col-${col}`"
                class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
              >
                {{ col }}
              </th>
            </tr>
          </thead>
          <tbody class="relative" v-if="accounts.data && accounts.data.length">
            <tr
              v-for="(account, index) in accounts.data"
              :key="account._id"
              class="transition-colors duration-150 hover:bg-taxiye-50"
              :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
            >
              <!-- ID -->
              <td class="px-5 py-4 whitespace-no-wrap">
                <span class="font-mono text-xs text-gray-500">{{ account._id }}</span>
              </td>
              <!-- Full name -->
              <td class="px-5 py-4 whitespace-no-wrap">
                <div class="flex items-center space-x-3">
                  <span
                    class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                    :class="avatarClass(personLabel(account))"
                  >{{ initials(personLabel(account)) }}</span>
                  <p class="font-medium text-gray-900">
                    {{ personLabel(account, "Unknown") }}
                  </p>
                </div>
              </td>
              <!-- Email -->
              <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                {{ account.email }}
              </td>
              <!-- Role -->
              <td class="px-5 py-4 whitespace-no-wrap">
                <span
                  class="inline-flex px-2 py-1 mr-2 text-xs font-medium text-teal-700 rounded-md bg-teal-100"
                  v-for="role in account.roles"
                  :key="role._id"
                >{{ role.roleName }}</span>
              </td>
              <!-- Status -->
              <td class="px-5 py-4 whitespace-no-wrap">
                <span
                  class="inline-flex items-center px-2 py-1 text-xs font-semibold leading-none rounded-full"
                  :class="account.active ? 'text-green-700 bg-green-100' : 'text-gray-600 bg-gray-100'"
                >
                  <span
                    class="w-2 h-2 mr-2 rounded-full"
                    :class="account.active ? 'bg-green-500' : 'bg-gray-400'"
                  ></span>
                  {{ account.active ? "Active" : "Inactive" }}
                </span>
              </td>
              <!-- Actions -->
              <td class="px-5 py-4 text-right whitespace-no-wrap">
                <div @click.stop class="relative inline-block text-left">
                  <button
                    @click="toggleDropdown(index)"
                    type="button"
                    class="inline-flex items-center justify-center w-9 h-9 text-gray-500 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
                    aria-haspopup="true"
                    :aria-expanded="openDropdown === index"
                    :ref="
                      openDropdown === index
                        ? 'accounts-dropdown-trigger-' + ref
                        : ''
                    "
                  >
                    <i class="material-icons-round">more_vert</i>
                  </button>
                  <transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-in"
                    leave-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0"
                  >
                    <div
                      v-if="openDropdown === index"
                      class="absolute right-0 z-10 w-56 mt-1 origin-top-right bg-white border border-gray-100 shadow-lg rounded-xl"
                      :ref="
                        openDropdown === index ? 'accounts-dropdown-' + ref : ''
                      "
                    >
                      <div
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="options-menu"
                      >
                        <div class="py-1">
                          <router-link
                            :to="`accounts/${account._id}/edit`"
                            class="flex items-center px-4 py-2 space-x-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                            role="menuitem"
                          >
                            <i class="text-base material-icons-round">edit</i>
                            <span>Edit</span>
                          </router-link>
                          <router-link
                            :to="`accounts/${account._id}/reset-password`"
                            class="flex items-center px-4 py-2 space-x-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                            role="menuitem"
                          >
                            <i class="text-base material-icons-round">vpn_key</i>
                            <span>Reset Password</span>
                          </router-link>
                          <button
                            @click="toggleActive(account._id)"
                            type="button"
                            class="flex items-center w-full px-4 py-2 space-x-2 text-sm text-left text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                            role="menuitem"
                          >
                            <i class="text-base material-icons-round">{{ account.active ? "close" : "check" }}</i>
                            <span>{{ account.active ? "Make Inactive" : "Make Active" }}</span>
                          </button>
                        </div>
                        <div class="py-1 border-t border-gray-100">
                          <button
                            @click="openConfirmBox"
                            type="button"
                            class="flex items-center w-full px-4 py-2 space-x-2 text-sm text-left text-red-600 transition-colors duration-150 hover:bg-red-50 focus:outline-none"
                            role="menuitem"
                          >
                            <i class="text-base material-icons-round">delete</i>
                            <span>Delete</span>
                            <portal to="modals">
                              <transition
                                enter-active-class="transition duration-100 ease-out"
                                enter-class="transform scale-95 opacity-0"
                                enter-to-class="transform scale-100 opacity-100"
                                leave-active-class="transition duration-75 ease-in"
                                leave-class="transform scale-100 opacity-100"
                                leave-to-class="transform scale-95 opacity-0"
                              >
                                <div
                                  v-if="confirmOpen"
                                  @click="closeConfirmBox"
                                  class="absolute inset-0 z-40 flex items-center justify-center bg-black bg-opacity-25"
                                >
                                  <div
                                    @click.stop
                                    class="w-full max-w-lg overflow-hidden bg-white shadow-lg rounded-2xl"
                                  >
                                    <div class="bg-gray-100">
                                      <h3 class="px-6 py-4 font-semibold text-gray-600">
                                        Confirm Delete
                                      </h3>
                                    </div>
                                    <div class="flex items-center px-6 py-6 space-x-4">
                                      <span class="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-full bg-red-100">
                                        <i class="text-2xl text-red-500 material-icons-round">warning</i>
                                      </span>
                                      <div>
                                        <p class="text-gray-800">
                                          Are you sure you want to delete this account?
                                        </p>
                                        <p>
                                          <em class="text-sm text-gray-600">This action can not be undone!</em>
                                        </p>
                                      </div>
                                    </div>
                                    <hr />
                                    <div class="flex items-end justify-between px-6 py-4">
                                      <button
                                        @click="closeConfirmBox"
                                        type="button"
                                        class="px-4 py-2 text-gray-600 rounded-xl transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        @click="deleteAccount(account._id)"
                                        type="button"
                                        :disabled="deleting"
                                        class="flex items-center px-4 py-2 space-x-2 font-semibold border border-transparent shadow-sm rounded-xl"
                                        :class="
                                          deleting
                                            ? 'bg-red-100 text-red-400 cursor-not-allowed'
                                            : 'bg-red-500 text-white hover:bg-red-600'
                                        "
                                      >
                                        <i v-if="deleting" class="material-icons-round spin">sync</i>
                                        <i v-else class="material-icons-round">delete</i>
                                        <span>Delete</span>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </transition>
                            </portal>
                          </button>
                        </div>
                      </div>
                    </div>
                  </transition>
                </div>
              </td>
            </tr>
            <div
              v-if="loading"
              class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
            >
              <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
              <span class="invisible block">Loading...</span>
            </div>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="6" class="py-16 text-center bg-white">
                <div v-if="loading" class="inline-block">
                  <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                  <span class="invisible block">Loading...</span>
                </div>
                <div v-else class="flex flex-col items-center text-gray-400">
                  <span class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100">
                    <i class="text-3xl material-icons-round">inbox</i>
                  </span>
                  <span class="text-sm font-medium text-gray-500">No users found</span>
                  <span class="mt-1 text-xs text-gray-400">Try adjusting your search</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="flex items-end justify-between">
      <pagination
        class="flex space-x-2"
        :currentPage="currentPage"
        :total="total"
        @goto="goToPage"
        @setPerPage="setPerPage"
        :perPage="perPage"
        :start="start"
        :end="end"
        :count="paginator.count"
      ></pagination>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import PaginationMixin from "@/mixins/Pagination";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      accounts: [],
      openDropdown: false,
      perPage: 10,
      searchKey: "",
      loading: true,
      confirmOpen: false,
      deleting: false,
      ref: "",
    };
  },
  components: {
    Pagination,
  },
  created() {
    this.getAccounts();
  },
  mounted() {
    this.ref = Math.random().toString(36).substr(2, 5);
  },
  watch: {
    currentPage() {
      this.getAccounts();
    },
    openDropdown(newVal) {
      if (newVal !== false) {
        document.body.addEventListener("click", this.clickAwayHandler);
      } else {
        document.body.removeEventListener("click", this.clickAwayHandler);
      }
    },
  },
  methods: {
    toggleDropdown(index) {
      if (this.openDropdown === index) {
        this.openDropdown = false;
        document.body.removeEventListener("click", this.clickAwayHandler);
      } else {
        this.openDropdown = index;
        document.body.addEventListener("click", this.clickAwayHandler);
      }
    },
    personLabel(person, fallback = "") {
      if (!person) return fallback;
      const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
      if (!name) return fallback;
      return name
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarClass(name) {
      const palette = [
        "bg-taxiye-100 text-taxiye-600",
        "bg-indigo-50 text-indigo-600",
        "bg-blue-50 text-blue-600",
        "bg-green-50 text-green-600",
        "bg-purple-50 text-purple-600",
        "bg-pink-50 text-pink-600",
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) {
        hash = (hash + name.charCodeAt(i)) % palette.length;
      }
      return palette[hash];
    },
    getAccounts() {
      this.loading = true;
      var path = "accounts?page=" + this.currentPage + "&limit=" + this.perPage;

      if (this.searchKey) {
        path += "&q=" + this.searchKey;
      }
      this.$misc
        .get(path)
        .then((response) => {
          this.accounts = response.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
    openConfirmBox() {
      this.confirmOpen = true;
    },
    closeConfirmBox() {
      this.confirmOpen = false;
    },
    deleteAccount(id) {
      this.deleting = true;
      this.$misc
        .delete(`accounts/${id}`)
        .then((result) => {
          window.flash({
            type: "success",
            body: "User was deleted successfully!",
          });
          this.getAccounts();
          console.log(result);
        })
        .catch((err) => {
          window.flash({
            type: "error",
            body: "Failed to delete the user!",
          });
          console.log(err);
        })
        .finally(() => {
          this.deleting = false;
          this.confirmOpen = false;
          this.openDropdown = false;
          this.getAccounts();
        });
    },
    search: _.debounce(function (e) {
      this.searchKey = e.target.value.trim();
      this.getAccounts();
    }, 500),
    toggleActive(id) {
      const currentStatus = this.accounts.data.find(
        (account) => account._id === id
      ).active;
      this.$misc
        .patch(`/accounts/${id}`, {
          active: !currentStatus,
        })
        .then(() => {
          window.flash({
            type: "success",
            body: currentStatus
              ? "User is now Inactive!"
              : "User is now Active!",
          });
          this.getAccounts();
        })
        .catch((err) => {
          window.flash({
            type: "error",
            body: "Failed to update user status!",
          });
          console.log(err);
        })
        .finally(() => (this.openDropdown = false));
    },
    clickAwayHandler(e) {
      if (
        (this.$refs["accounts-dropdown-trigger-" + this.ref] &&
          this.$refs["accounts-dropdown-trigger-" + this.ref][0].contains(
            e.target
          )) ||
        this.$refs["accounts-dropdown-" + this.ref] === e.target
      ) {
        return;
      }

      this.openDropdown = false;
    },
  },
  beforeDestroy() {
    document.body.removeEventListener("click", this.clickAwayHandler);
  },
};
</script>
